import { getSysMessagePeopleList } from '@/services/message/MessageController';
import { BellOutlined, CheckCircleOutlined, ClockCircleOutlined, UserOutlined, ProjectOutlined, BuildOutlined, InboxOutlined } from '@ant-design/icons';
import { ProList } from '@ant-design/pro-components';
import { Badge, Modal, Tabs, Tag, Typography, Space, Divider, Button, Empty } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';
import { createStyles } from 'antd-style';
import { PlatformEnum } from '@/settings/enum';
import store from '@/store';
import { getCompanyDetail } from '@/services/financial/FinancialController';

// 创建样式
const useStyles = createStyles(({ token }) => ({
  modalHeader: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px 8px 0 0',
    padding: '16px 24px 0',
    margin: 0,
  },
  modalBody: {
    padding: '16px 24px',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  tabLabel: {
    padding: '8px 16px',
    fontWeight: 500,
    position: 'relative',
    fontSize: '14px',
  },
  unreadMessage: {
    backgroundColor: '#f0f9ff !important',
    borderLeft: '3px solid #1890ff !important',
    '&:hover': {
      backgroundColor: '#e6f7ff !important',
    },
  },
  readMessage: {
    backgroundColor: '#fafafa !important',
    opacity: 0.8,
    '&:hover': {
      backgroundColor: '#f5f5f5 !important',
    },
  },
}));

// 消息数据类型定义
interface MessageRecord {
  id: string | number;
  messageTitle: string;
  messageText: string;
  messageState: number;
  message_category: string;
  created_time: string;
  project_name?: string;
  company_name?: string;
  username?: string;
  company_id?: string;
  project_id?: string;
}

// API响应类型定义
interface MessageResponse {
  data: MessageRecord[];
  total: number;
}

/**
 * NotificationPopover 组件
 * 通知、消息、待办的展示效果，支持分页
 */
const NotificationPopover: React.FC = () => {
  // 初始化dayjs插件和语言
  dayjs.extend(relativeTime);
  dayjs.locale('zh-cn');

  const { styles } = useStyles();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('notification');
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<MessageRecord[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 5,
    total: 0,
  });
  const [unreadCounts, setUnreadCounts] = useState({
    notification: 0,
    message: 0,
    todo: 0,
  });

  // 进入公司
  function enterCompany(data: any) {
    store.dispatch({ type: `${PlatformEnum.NAME}/setCompanyInfo`, payload: data });
    window.location.href = '/';
  }

  // 进入项目
  function enterProject(data: any) {
    const newWindow = window.open('/', '_blank'); // 打开新页面

    if (newWindow) {
      // 注入脚本到新页面，在新页面中处理数据并删除临时变量
      newWindow.onload = () => {
        newWindow.sessionStorage.setItem('temp_info', JSON.stringify(data)); // 传递临时键名
      };
    }
  }

  // 处理公司点击
  async function handleCompanyClick(record: MessageRecord) {
    if (!record.company_id) return;

    try {
      // 调用API获取完整的公司数据
      const companyDetail = await getCompanyDetail({ id: record.company_id });

      if (companyDetail) {
        // 使用获取到的完整公司数据
        enterCompany(companyDetail);
      } else {
        // 如果API调用失败，使用基础数据
        const companyData = {
          id: record.company_id,
          company: record.company_name,
        };
        enterCompany(companyData);
      }
    } catch (error) {
      console.error('获取公司详情失败:', error);
      // 如果API调用失败，使用基础数据
      const companyData = {
        id: record.company_id,
        company: record.company_name,
      };
      enterCompany(companyData);
    }
  }

  // 处理项目点击
  function handleProjectClick(record: MessageRecord) {
    if (!record.project_id) return;

    // 如果project_id为0，则跳转到公司
    if (record.project_id === 0 || record.project_id === '0') {
      handleCompanyClick(record);
      return;
    }

    const projectData = {
      id: record.project_id,
      project_name: record.project_name,
      company_id: record.company_id,
      company_name: record.company_name,
    };

    enterProject(projectData);
  }

  // 获取数据
  const fetchData = useCallback(
    async (tab: string, page: number) => {
      setLoading(true);
      try {
        const response = await getSysMessagePeopleList({
          messageType: tab, // 根据不同的 Tab 传递不同的类型
          messageState:'1',
          page,
          pageSize: pagination.pageSize,
        }) as MessageResponse;
        setData(response.data || []); // 更新为返回的数据
        setPagination(prev => ({
          ...prev,
          page: page, // 更新当前页码
          total: response.total || 0, // 更新总数
        }));
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize],
  );

  // 获取未读消息总数
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const types = ['notification', 'message', 'todo'];
      const counts: Record<string, number> = {};

      for (const type of types) {
        const response = await getSysMessagePeopleList({
          messageType: type,
          messageState:'1',
        }) as MessageResponse;
        // 假设未读消息有某个字段标识，这里用总数作为示例
        counts[type] = response.total || 0;
      }

      setUnreadCounts(counts);
    } catch (error) {
      console.error('Failed to fetch unread counts:', error);
    }
  }, []);

  // 切换 tab 时请求数据
  useEffect(() => {
    // 切换标签时重置到第一页
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchData(activeTab, 1);
  }, [activeTab]); // 只依赖 activeTab，避免 pagination 触发无限循环

  // 获取未读消息总数
  useEffect(() => {
    fetchUnreadCounts();
  }, []);

  // 控制 Modal 可见性
  const showModal = () => {
    setOpen(true);
    fetchUnreadCounts(); // 打开时重新获取未读数量
  };

  const handleCancel = () => {
    setOpen(false);
  };

  // Tabs items
  const items = [
    { key: 'notification', label: `通知` },
    { key: 'message', label: `消息` },
    { key: 'todo', label: `待办` },
  ];

  // 计算总未读数
  const totalUnread = unreadCounts.notification + unreadCounts.message + unreadCounts.todo;

  // 格式化时间显示
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    try {
      return dayjs(timeStr).fromNow();
    } catch {
      return timeStr;
    }
  };

  // 获取消息状态标签
  const getMessageStateTag = (state: number) => {
    switch (state) {
      case 1:
        return <Tag icon={<ClockCircleOutlined />} color="default">未读</Tag>;
      case 2:
        return <Tag icon={<CheckCircleOutlined />} color="success">已读</Tag>;
      default:
        return null;
    }
  };

  // 获取消息类型标签
  const getMessageTypeTag = (category: string) => {
    switch (category) {
      case 'notification':
        return <Tag color="blue">通知</Tag>;
      case 'message':
        return <Tag color="green">消息</Tag>;
      case 'todo':
        return <Tag color="orange">待办</Tag>;
      default:
        return <Tag color="default">其他</Tag>;
    }
  };

  return (
    <>
      <Badge
        count={totalUnread > 0 ? totalUnread : 0}
        showZero={false}
        offset={[-25, -6]}
      >
        <BellOutlined
          style={{ fontSize: '18px', color: '#08c', cursor: 'pointer' }}
          onClick={showModal}
        />
      </Badge>

      <Modal
        title="消息中心"
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={1000}
        bodyStyle={{ padding: 0, maxHeight: '600px', overflow: 'hidden' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          centered
          tabBarStyle={{
            margin: 0,
            padding: '16px 24px 0',
            background: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
          }}
          items={items.map((tab) => ({
            key: tab.key,
            label: (
              <div className={styles.tabLabel}>
                {tab.label}
                {unreadCounts[tab.key as keyof typeof unreadCounts] > 0 && (
                  <Badge
                    count={unreadCounts[tab.key as keyof typeof unreadCounts]}
                    style={{
                      position: 'absolute',
                      top: '-30px',
                      right: '-20px',
                      backgroundColor: '#ff4d4f'
                    }}
                    size="small"
                    offset={[0, 0]}
                  />
                )}
              </div>
            ),
            children: (
              <div className={styles.modalBody}>
                {data.length === 0 && !loading ? (
                  <Empty
                    description="暂无消息"
                    image={<InboxOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />}
                    style={{ marginTop: '40px', marginBottom: '40px' }}
                  />
                ) : (
                  <ProList
                    rowKey="id"
                    dataSource={data}
                    loading={loading}
                    pagination={{
                      current: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      size: 'small',
                      showSizeChanger: false,
                      showQuickJumper: true,
                      showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
                      style: {
                        marginTop: '16px',
                        padding: '8px 0',
                        textAlign: 'center',
                      },
                      onChange: (page) => {
                        setPagination((prev) => ({
                          ...prev,
                          page,
                        }));
                        fetchData(activeTab, page);
                      },
                    }}
                    metas={{
                      title: {
                        render: (_, record) => (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px',marginLeft: '15px' }}>
                            <Typography.Text strong style={{ fontSize: '14px' }}>
                              {record.messageTitle}
                            </Typography.Text>
                            {getMessageStateTag(record.messageState)}
                            {getMessageTypeTag(record.message_category)}
                          </div>
                        ),
                      },
                      description: {
                        render: (_, record) => (
                          <div style={{ marginTop: '8px',marginLeft: '10px' }}>
                            <Typography.Paragraph
                              ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}
                              style={{ marginBottom: '8px', fontSize: '13px', color: '#666' }}
                            >
                              {record.messageText}
                            </Typography.Paragraph>

                            <Space size="small" split={<Divider type="vertical" />}>
                              {record.project_name && (
                                <Space size={4}>
                                  <ProjectOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                                  <Typography.Text
                                    style={{
                                      fontSize: '12px',
                                      color: '#1890ff',
                                      cursor: 'pointer',
                                      textDecoration: 'underline'
                                    }}
                                    onClick={() => handleProjectClick(record)}
                                  >
                                    {record.project_name}
                                  </Typography.Text>
                                </Space>
                              )}

                              {record.company_name && (
                                <Space size={4}>
                                  <BuildOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                                  <Typography.Text
                                    style={{
                                      fontSize: '12px',
                                      color: '#52c41a',
                                      cursor: 'pointer',
                                      textDecoration: 'underline'
                                    }}
                                    onClick={() => handleCompanyClick(record)}
                                  >
                                    {record.company_name}
                                  </Typography.Text>
                                </Space>
                              )}

                              {record.username && (
                                <Space size={4}>
                                  <UserOutlined style={{ color: '#fa8c16', fontSize: '12px' }} />
                                  <Typography.Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    {record.username}
                                  </Typography.Text>
                                </Space>
                              )}

                              <Space size={4}>
                                <ClockCircleOutlined style={{ color: '#722ed1', fontSize: '12px' }} />
                                <Typography.Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                  {formatTime(record.created_time)}
                                </Typography.Text>
                              </Space>
                            </Space>
                          </div>
                        ),
                      },
                    }}
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '8px',
                      background: '#fafafa',
                    }}
                    itemLayout="vertical"
                    showActions="hover"
                    rowClassName={(record:any) =>
                      record.messageState === 0 ? styles.unreadMessage : styles.readMessage
                    }
                  />
                )}
              </div>
            ),
          }))}
        />
      </Modal>
    </>
  );
};

export default NotificationPopover;